/**
 * Single source of truth for per-page SEO.
 *
 * Consumed by:
 *   - <SEO /> runtime component (react-helmet-async) so users see correct
 *     title/description in their browser tab and history.
 *   - scripts/prerender.js at build time, which writes static <route>/index.html
 *     files with these tags inlined so social-media scrapers (Facebook,
 *     LinkedIn, Twitter, Google) get per-page metadata without running JS.
 *
 * Keep this file plain ES module syntax that's also valid in Node (no JSX,
 * no React imports) so the prerender script can import it directly.
 */

export const SITE_URL = 'https://poettik.com';
export const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;
export const SITE_NAME = 'Poettik';
export const DEFAULT_TITLE =
  'Poettik — AI Script & Hook Generator for TikTok Shop & UGC Creators';
export const DEFAULT_DESCRIPTION =
  'Poettik generates scroll-stopping hooks, scripts and filming ideas for TikTok Shop affiliates and UGC creators. Start a 3-day free trial — no card required.';

/**
 * Map of public, indexable pages.
 * Routes NOT listed here either:
 *   - Fall back to default tags (above), or
 *   - Are explicitly marked noindex (handled in the page component itself).
 */
export const PAGE_SEO = {
  '/': {
    title: 'AI Script & Hook Generator for TikTok Shop & UGC Creators',
    description:
      'Generate scroll-stopping hooks, scripts and filming ideas for TikTok Shop and UGC content in seconds. Built for affiliates and creators. Start your 3-day free trial.',
  },
  '/pricing': {
    title: 'Plans & Pricing',
    description:
      'Simple monthly pricing for TikTok Shop and UGC creators. Basic £9.99/mo, Pro £15.99/mo. Cancel anytime — no lock-in.',
  },
  '/faq': {
    title: 'Frequently Asked Questions',
    description:
      'Answers to common questions about Poettik — pricing, trial, AI content generation, refunds and the affiliate programme.',
  },
  '/about': {
    title: 'About',
    description:
      'Poettik is an AI script and hook generator built for UK TikTok Shop affiliates and UGC creators. Discover the story and the team behind the product.',
  },
  '/support': {
    title: 'Contact Support',
    description:
      'Get in touch with the Poettik team for account help, billing questions or product feedback.',
  },
  '/privacy': {
    title: 'Privacy Policy',
    description:
      'How Poettik collects, uses and protects your personal data — GDPR-compliant for UK and EU users.',
  },
  '/cookies': {
    title: 'Cookie Policy',
    description:
      'How Poettik uses cookies to improve your experience, and how to manage your preferences.',
  },
  '/login': {
    title: 'Sign In',
    description:
      'Sign in to your Poettik account to generate viral content for TikTok Shop and UGC creators.',
  },
  '/register': {
    title: 'Create Account',
    description:
      'Create a free Poettik account and start a 3-day free trial. Generate viral hooks and scripts for TikTok Shop and UGC content.',
  },
  '/affiliate-interest': {
    title: 'Earn with Poettik — Affiliate Programme',
    description:
      'Join the Poettik affiliate programme. Earn up to 40% recurring commission on every monthly subscription you refer.',
  },
  '/free-violation-appeal': {
    title: 'Free TikTok Shop Violation Appeal Tool',
    description:
      "Got a TikTok Shop violation? Use Poettik's free appeal tool for an honest assessment, your estimated success chance, and a ready-to-copy appeal — plus how similar appeals have gone for other creators. No account needed.",
  },
};

/**
 * Compute the final tags for a given path. If a path is missing from
 * PAGE_SEO, fall back to defaults. Page components may pass overrides
 * (title/description props) which take precedence.
 */
export function resolveSEO(pathname, overrides = {}) {
  const entry = PAGE_SEO[pathname] || {};
  const title = overrides.title || entry.title;
  const description = overrides.description || entry.description || DEFAULT_DESCRIPTION;
  return {
    title: title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE,
    description,
    image: overrides.image || DEFAULT_IMAGE,
    url: `${SITE_URL}${pathname || '/'}`,
  };
}
